import { InkPoint, InkStroke } from '../models/draft-form.model';

type CurveSegmentCallback = (
  cp1x: number,
  cp1y: number,
  cp2x: number,
  cp2y: number,
  px: number,
  py: number
) => void;

/**
 * Iterate Catmull-Rom spline segments over normalized 0-1 points.
 * Calls `cb` once per curve segment with the two control points and end point.
 */
function catmullRom(points: InkPoint[], cb: CurveSegmentCallback): void {
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(i - 1, 0)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(i + 2, points.length - 1)];

    cb(
      p1.x + (p2.x - p0.x) / 6,
      p1.y + (p2.y - p0.y) / 6,
      p2.x - (p3.x - p1.x) / 6,
      p2.y - (p3.y - p1.y) / 6,
      p2.x,
      p2.y
    );
  }
}

/**
 * Render a stroke on a canvas context using Catmull-Rom spline interpolation.
 * Points are in normalized 0-1 coordinates; width/height scale them to pixels.
 */
export function renderStrokeOnCanvas(
  ctx: CanvasRenderingContext2D,
  stroke: InkStroke,
  width: number,
  height: number,
  color: string,
  lineWidth: number
): void {
  const points = stroke.points;
  if (points.length === 0) return;

  ctx.beginPath();
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  ctx.moveTo(points[0].x * width, points[0].y * height);

  if (points.length <= 2) {
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x * width, points[i].y * height);
    }
  } else {
    catmullRom(points, (cp1x, cp1y, cp2x, cp2y, px, py) => {
      ctx.bezierCurveTo(
        cp1x * width,
        cp1y * height,
        cp2x * width,
        cp2y * height,
        px * width,
        py * height
      );
    });
  }

  ctx.stroke();
}

/**
 * Generate an SVG path `d` attribute from normalized ink points.
 * Uses Catmull-Rom spline interpolation for smooth curves.
 */
export function generateSvgPath(points: InkPoint[]): string {
  if (points.length === 0) {
    return '';
  }

  if (points.length === 1) {
    const p = points[0];
    return `M${p.x},${p.y} L${p.x},${p.y}`;
  }

  if (points.length === 2) {
    return `M${points[0].x},${points[0].y} L${points[1].x},${points[1].y}`;
  }

  let d = `M${points[0].x},${points[0].y}`;

  catmullRom(points, (cp1x, cp1y, cp2x, cp2y, px, py) => {
    d += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${px},${py}`;
  });

  return d;
}
